// ─────────────────────────────────────────────────────────────────────────────
// controllers/integrationsController.js
//
// SCOPE-AWARE FRONTEND CONTROLLER
//
// PLATFORM SCOPE  (super_admin)
//   → Seed, Create, Update metadata, Enable/Disable, Delete platform records
//   → config = super_admin's own service credentials
//   → toggle/enable/disable = controls visibility for ALL admins globally
//
// COMPANY SCOPE   (admin)
//   → Reads platform cards (isEnabled: true) merged with company config
//   → Writes ONLY to company record config (their own credentials)
//   → Platform config is NEVER shown — only blank template fields
//
// API uses platform record _id everywhere.
// Backend auto-creates company shadow record on first config access.
// ─────────────────────────────────────────────────────────────────────────────
import apiService from '../services/apiService';
import { authController } from './authController';

// ── Role helpers ──────────────────────────────────────────────────────────────
const _getUser = () =>
  authController.getCurrentUser?.() || JSON.parse(localStorage.getItem('user') || 'null');
const _getRole = () => _getUser()?.role || null;

const _guard = (allowedRoles) => {
  const role = _getRole();
  if (!role)                        return { blocked: true, reason: 'Not authenticated. Please log in.' };
  if (!allowedRoles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${allowedRoles.join(' or ')}` };
  return { blocked: false };
};

class IntegrationsController {

  _base() { return 'integrations'; }

  // ── Auth helpers (exposed for UI) ─────────────────────────────────────────
  getCurrentUser() { return _getUser(); }
  getCurrentRole() { return _getRole(); }
  isSuperAdmin()   { return _getRole() === 'super_admin'; }
  isAdmin()        { return ['admin', 'super_admin'].includes(_getRole()); }

  // ─────────────────────────────────────────────────────────────────────────
  // GET ALL
  //
  // super_admin → all platform records (masked config), ?enabled filter works
  // admin       → enabled platform cards merged with company record
  //               (company config shown, platform config hidden)
  //
  // opts: { category, search, enabled, configured, premium, page, limit, sortBy, sortOrder }
  // ─────────────────────────────────────────────────────────────────────────
  async getIntegrations(opts = {}) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const params = new URLSearchParams();
      if (opts.category !== undefined)   params.set('category',   opts.category);
      if (opts.search)                   params.set('search',     opts.search);
      if (opts.enabled !== undefined)    params.set('enabled',    opts.enabled);
      if (opts.configured !== undefined) params.set('configured', opts.configured);
      if (opts.premium !== undefined)    params.set('premium',    opts.premium);
      if (opts.sortBy)                   params.set('sortBy',     opts.sortBy);
      if (opts.sortOrder)                params.set('sortOrder',  opts.sortOrder);
      params.set('limit', opts.limit || 100);
      params.set('page',  opts.page  || 1);

      const res = await apiService.get(`${this._base()}?${params.toString()}`);
      return {
        success:    true,
        data:       res.data       || [],
        total:      res.total      || 0,
        totalPages: res.totalPages || 1,
        scope:      res.data?.[0]?.scope || (this.isSuperAdmin() ? 'platform' : 'company'),
      };
    } catch (err) {
      console.error('[Integrations] getIntegrations:', err.message);
      return { success: false, data: [], message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET BY ID  (platform record _id)
  // super_admin → platform record with masked config
  // admin       → merged card (company config, no platform config)
  // ─────────────────────────────────────────────────────────────────────────
  async getIntegrationById(id) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`${this._base()}/${id}`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET BY SLUG
  // ─────────────────────────────────────────────────────────────────────────
  async getIntegrationBySlug(slug) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`${this._base()}/slug/${slug}`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SEED — SUPER_ADMIN ONLY
  // Creates platform-level records — no companyId needed
  // ─────────────────────────────────────────────────────────────────────────
  async seedIntegrations() {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`${this._base()}/seed`);
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CREATE — SUPER_ADMIN ONLY
  // Creates platform-level integration (no companyId)
  // body: { name, slug, category, description, logo, website,
  //         isPremium, config, configTemplate, limits }
  // ─────────────────────────────────────────────────────────────────────────
  async createIntegration(data) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`${this._base()}`, data);
      return { success: true, data: res.data, message: res.message || 'Integration created' };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // UPDATE METADATA — SUPER_ADMIN ONLY
  // Updates platform record: name, description, logo, category,
  //                          limits, configTemplate, isPremium
  // ─────────────────────────────────────────────────────────────────────────
  async updateIntegration(id, updates) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}`, updates);
      return { success: true, data: res.data, message: res.message || 'Integration updated' };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE — SUPER_ADMIN ONLY
  // Deletes platform record + all company shadow records for that slug
  // ─────────────────────────────────────────────────────────────────────────
  async deleteIntegration(id) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.delete(`${this._base()}/${id}`);
      return { success: true, message: res.message || 'Integration deleted' };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TOGGLE VISIBILITY — SUPER_ADMIN ONLY
  // isEnabled: true  → card visible to ALL admins globally
  // isEnabled: false → card hidden from ALL admins globally
  // ─────────────────────────────────────────────────────────────────────────
  async toggleIntegration(id) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/toggle`);
      return { success: true, data: res.data, isEnabled: res.isEnabled, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ENABLE / DISABLE — SUPER_ADMIN ONLY
  // ─────────────────────────────────────────────────────────────────────────
  async enableIntegration(id) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/enable`);
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  async disableIntegration(id) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/disable`);
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET CONFIG (unmasked)
  //
  // super_admin → platform record config (super_admin's credentials, unmasked)
  // admin       → company record config (company's own credentials, unmasked)
  //               Backend auto-creates blank company record if first access
  //
  // Response: { success, scope: 'platform'|'company', data: { config, ... } }
  // ─────────────────────────────────────────────────────────────────────────
  async getFullConfig(id) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`${this._base()}/${id}/config`);
      return { success: true, scope: res.scope, data: res.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // UPDATE CONFIG
  //
  // super_admin → writes to platform record config (super_admin's credentials)
  // admin       → writes ONLY to company record config (company's credentials)
  //               Only configTemplate keys are accepted — no extra fields
  //               Backend auto-creates company record if needed
  // ─────────────────────────────────────────────────────────────────────────
  async updateConfig(id, config) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/config`, { config });
      return {
        success:      true,
        scope:        res.scope,
        data:         res.data,
        isConfigured: res.isConfigured,
        message:      res.message,
      };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RESET CONFIG
  //
  // super_admin → clears platform record config
  // admin       → clears ONLY company record config
  //               Does NOT affect other companies or the platform record
  // ─────────────────────────────────────────────────────────────────────────
  async resetConfig(id) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.delete(`${this._base()}/${id}/config`);
      return { success: true, scope: res.scope, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TEST CONNECTION
  //
  // super_admin → tests using platform record config
  // admin       → tests using company record config (must be isConfigured)
  // ─────────────────────────────────────────────────────────────────────────
  async testConnection(id) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`${this._base()}/${id}/test`);
      return { success: res.success, message: res.message, latency: res.latency, data: res.data };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STATISTICS
  //
  // super_admin → platform-level stats (all platform records)
  // admin       → company-level stats (enabled cards + company config status)
  //
  // Response: { success, scope: 'platform'|'company', data: {...} }
  // ─────────────────────────────────────────────────────────────────────────
  async getStatistics() {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: { total: 0, enabled: 0, configured: 0, premium: 0 }, message: g.reason };
    try {
      const res = await apiService.get(`${this._base()}/stats`);
      return { success: true, scope: res.scope, data: res.data };
    } catch (err) {
      return { success: false, data: { total: 0, enabled: 0, configured: 0, premium: 0 }, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CATEGORIES
  // super_admin → all; admin → only enabled
  // ─────────────────────────────────────────────────────────────────────────
  async getCategories() {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get(`${this._base()}/categories`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // WEBHOOKS
  // super_admin → writes to platform record
  // admin       → writes to company record (auto-created if needed)
  // ─────────────────────────────────────────────────────────────────────────
  async addWebhook(id, { event, url, secret }) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`${this._base()}/${id}/webhooks`, { event, url, secret });
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  async removeWebhook(id, webhookId) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.delete(`${this._base()}/${id}/webhooks/${webhookId}`);
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SYNC SETTINGS
  // super_admin → platform record; admin → company record
  // ─────────────────────────────────────────────────────────────────────────
  async updateSyncSettings(id, { enabled, frequency }) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/sync`, { enabled, frequency });
      return { success: true, data: res.data, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // BULK UPDATE — SUPER_ADMIN ONLY
  // Operates on platform records only
  // action: 'enable' | 'disable'
  // ─────────────────────────────────────────────────────────────────────────
  async bulkUpdate(ids, action) {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/bulk`, { ids, action });
      return { success: true, modified: res.modified, message: res.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // INCREMENT STATS — always on company record
  // ─────────────────────────────────────────────────────────────────────────
  async incrementStats(id, success = true) {
    const g = _guard(['admin', 'super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`${this._base()}/${id}/stats`, { success });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, message: err.message };
    }
  }
}

export const integrationsController = new IntegrationsController();
export default integrationsController;